import {
  sameUserId,
  getJoinedCount,
  getBudgetFillPercent,
  getCreatorBudget,
  getRemainingCounterCapacity,
} from "./p2pHelpers";

export const REQUEST_VIEWS = [
  { id: "all", label: "All" },
  { id: "inbox", label: "Inbox" },
  { id: "outbox", label: "Outbox" },
];

export const getRefId = (ref) => {
  if (ref == null || ref === "") return "";
  if (typeof ref === "object" && ref !== null && ref._id != null) {
    return String(ref._id);
  }
  return String(ref);
};

export const filterSentP2PBets = (bets = []) =>
  (Array.isArray(bets) ? bets : []).filter(
    (bet) => Array.isArray(bet?.invites) && bet.invites.length > 0
  );

export const filterReceivedP2PBets = (bets = [], userId) => {
  if (!userId) return [];
  return (Array.isArray(bets) ? bets : []).filter((bet) =>
    bet?.invites?.some((invite) => sameUserId(invite.user, userId))
  );
};

export const getUserInviteOnBet = (bet, userId) => {
  if (!bet?.invites || !userId) return null;
  return bet.invites.find((invite) => sameUserId(invite.user, userId)) || null;
};

export const getInviteStatus = (bet, userId) => {
  const invite = getUserInviteOnBet(bet, userId);
  return invite?.status || "";
};

export const isInvitePending = (status) => {
  const key = String(status || "").toLowerCase();
  return key === "pending" || key === "";
};

export const formatInviteStatus = (status) => {
  const key = String(status || "pending").toLowerCase();
  if (key === "accepted") return "Accepted";
  if (key === "rejected") return "Rejected";
  return "Pending";
};

export const inviteStatusTone = (status) => {
  const key = String(status || "").toLowerCase();
  if (key === "accepted") return "success";
  if (key === "rejected") return "danger";
  if (isInvitePending(status)) return "pending";
  return "muted";
};

export const countPendingP2PInvites = (bets = [], userId, direction) => {
  if (direction === "sent") {
    return filterSentP2PBets(bets).reduce((sum, bet) => {
      const pending =
        bet.invites?.filter((i) => isInvitePending(i.status)).length || 0;
      return sum + pending;
    }, 0);
  }
  return filterReceivedP2PBets(bets, userId).filter((bet) =>
    isInvitePending(getInviteStatus(bet, userId))
  ).length;
};

export const p2pInviteSummary = (bet) => {
  const invites = Array.isArray(bet?.invites) ? bet.invites : [];
  const pending = invites.filter((i) => isInvitePending(i.status)).length;
  const accepted = invites.filter(
    (i) => String(i.status || "").toLowerCase() === "accepted"
  ).length;
  const rejected = invites.filter(
    (i) => String(i.status || "").toLowerCase() === "rejected"
  ).length;
  return { pending, accepted, rejected, total: invites.length };
};

export const fixtureTeamsLabel = (fixture) => {
  const home = fixture?.teams?.home?.name || "Home";
  const away = fixture?.teams?.away?.name || "Away";
  return `${home} vs ${away}`;
};

export const p2pJoinedLabel = (bet) => {
  const joined = getJoinedCount(bet);
  const cap = getRemainingCounterCapacity(bet);
  return `${joined} in · Cash ${cap.toLocaleString()} left`;
};

/** Joined count as shown on request cards. */
export const p2pCardJoinedCount = (bet, direction) => {
  if (direction === "sent") {
    return getJoinedCount(bet);
  }
  const derived = getJoinedCount(bet);
  if (derived > 0) return derived;
  return Number(bet?.joinedPeople || 0);
};

export const p2pCardFillPercent = (bet) => getBudgetFillPercent(bet);

const itemSortTime = (...dates) => {
  for (const d of dates) {
    const t = new Date(d || 0).getTime();
    if (t > 0) return t;
  }
  return 0;
};

export const buildRequestItems = ({
  userP2PBets = [],
  marketP2PBets = [],
  userId,
}) => {
  const items = [];

  filterSentP2PBets(userP2PBets).forEach((bet) => {
    const fixture = bet?.fixture || {};
    const joined = p2pCardJoinedCount(bet, "sent");
    const fillPercent = p2pCardFillPercent(bet);
    const remainingCapacity = getRemainingCounterCapacity(bet);
    items.push({
      id: `p2p-sent-${getRefId(bet._id)}`,
      kind: "p2p",
      direction: "sent",
      view: "outbox",
      needsReply: false,
      awaitingOthers: fillPercent < 100,
      status: "sent",
      statusLabel: `${fillPercent}% matched`,
      statusTone: fillPercent < 100 ? "pending" : "success",
      title: fixtureTeamsLabel(fixture),
      subtitle: `${joined} counter${joined === 1 ? "" : "s"} · Cash ${remainingCapacity.toLocaleString()} left`,
      person: null,
      amount: getCreatorBudget(bet),
      expiresAt: fixture?.date || null,
      payload: { type: "p2p", bet, direction: "sent" },
      sortTime: itemSortTime(bet.updatedAt, bet.createdAt, fixture?.date),
    });
  });

  filterReceivedP2PBets(marketP2PBets, userId).forEach((bet) => {
    const inviteStatus = getInviteStatus(bet, userId);
    const status = String(inviteStatus || "pending").toLowerCase();
    const pending = isInvitePending(inviteStatus);
    const fixture = bet?.fixture || {};
    const joined = p2pCardJoinedCount(bet, "received");
    const remainingCapacity = getRemainingCounterCapacity(bet);
    items.push({
      id: `p2p-received-${getRefId(bet._id)}`,
      kind: "p2p",
      direction: "received",
      view: pending ? "inbox" : "all",
      needsReply: pending,
      awaitingOthers: false,
      status,
      statusLabel: formatInviteStatus(inviteStatus),
      statusTone: inviteStatusTone(inviteStatus),
      title: fixtureTeamsLabel(fixture),
      subtitle: `${joined} counter${joined === 1 ? "" : "s"} · Cash ${remainingCapacity.toLocaleString()} left`,
      person: null,
      amount: getCreatorBudget(bet),
      expiresAt: fixture?.date || null,
      payload: { type: "p2p", bet, direction: "received" },
      sortTime: itemSortTime(bet.updatedAt, bet.createdAt, fixture?.date),
    });
  });

  return items.sort((a, b) => {
    if (a.needsReply !== b.needsReply) return a.needsReply ? -1 : 1;
    if (a.awaitingOthers !== b.awaitingOthers) return a.awaitingOthers ? -1 : 1;
    return b.sortTime - a.sortTime;
  });
};

export const filterRequestItems = (items, { view, search }) => {
  let list = items;

  if (view === "inbox") {
    list = list.filter((item) => item.needsReply);
  } else if (view === "outbox") {
    list = list.filter((item) => item.direction === "sent");
  }

  const q = String(search || "").trim().toLowerCase();
  if (q) {
    list = list.filter(
      (item) =>
        item.title.toLowerCase().includes(q) ||
        item.subtitle.toLowerCase().includes(q) ||
        String(item.person || "").toLowerCase().includes(q)
    );
  }

  return list;
};

export const countInboxItems = (items) => items.filter((i) => i.needsReply).length;

export const countOutboxItems = (items) =>
  items.filter((i) => i.direction === "sent").length;

export const groupQueueItems = (items) => {
  const needsReply = items.filter((i) => i.needsReply);
  const waiting = items.filter((i) => !i.needsReply && i.awaitingOthers);
  const rest = items.filter((i) => !i.needsReply && !i.awaitingOthers);
  const groups = [];
  if (needsReply.length) groups.push({ id: "reply", label: "Needs your reply", items: needsReply });
  if (waiting.length) groups.push({ id: "waiting", label: "Waiting on others", items: waiting });
  if (rest.length) groups.push({ id: "done", label: "Resolved", items: rest });
  if (!groups.length) return [{ id: "empty", label: "", items: [] }];
  return groups;
};

export const defaultRequestView = (items) =>
  countInboxItems(items) > 0 ? "inbox" : "all";
